import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import { createSubscription, updateSubscriptionAccess, getAllSubscriptions } from '../../../services/supabaseService';

interface Subscription {
  id: string;
  user_id: string;
  user: {
    id: string;
    full_name: string;
    email: string;
  };
  plan_id: string;
  plan: {
    id: string;
    name: string;
    plan_name: string;
    price_vatu: number;
  };
  subscription_status: string;
  access_status: string;
  start_date: string;
  end_date: string;
  payment_due_date: string;
  access_locked: boolean;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  plan_type: string;
  price_vatu: number;
  duration_days: number;
}

interface GroupMember {
  id: string;
  user_id: string;
  full_name: string;
  email: string;
}

const SubscriptionManagement = () => {
  const { user, userProfile } = useAuth();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'expired' | 'pending'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [extendDays, setExtendDays] = useState<number>(30);

  useEffect(() => {
    fetchData();
  }, [filter, user, userProfile]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch subscription plans
      const { data: plans, error: plansError } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('price_vatu', { ascending: true });
      
      if (!plansError && plans) {
        setSubscriptionPlans(plans);
      }

      // Get admin's group members FIRST before fetching subscriptions
      let fetchedGroupMembers: GroupMember[] = [];
      
      if (user && userProfile?.role !== 'super_admin') {
        // Get groups where user is admin or creator
        const { data: adminGroups } = await supabase
          .from('group_members')
          .select('group_id')
          .eq('user_id', user.id)
          // group_members.role is a group-scoped role and does NOT include platform roles like 'group_admin'.
          .in('role', ['admin', 'clerk']);

        // Also check if user is creator of any groups
        const { data: createdGroups } = await supabase
          .from('groups')
          .select('id')
          .eq('created_by', user.id);

        const groupIds = new Set<string>();
        if (adminGroups) {
          adminGroups.forEach(g => groupIds.add(g.group_id));
        }
        if (createdGroups) {
          createdGroups.forEach(g => groupIds.add(g.id));
        }

        console.log('Group admin groups:', { 
          adminGroups: adminGroups?.length || 0, 
          createdGroups: createdGroups?.length || 0,
          groupIds: Array.from(groupIds),
          userId: user.id
        });

        if (groupIds.size > 0) {
          // Get members from admin's groups
          const { data: members, error: membersError } = await supabase
            .from('group_members')
            .select(`
              user_id,
              user_profiles!inner (
                id,
                full_name,
                email
              )
            `)
            .in('group_id', Array.from(groupIds));

          if (!membersError && members) {
            const uniqueMembers = new Map();
            members.forEach((m: any) => {
              if (m.user_profiles && !uniqueMembers.has(m.user_profiles.id)) {
                uniqueMembers.set(m.user_profiles.id, {
                  id: m.user_profiles.id,
                  user_id: m.user_profiles.id,
                  full_name: m.user_profiles.full_name,
                  email: m.user_profiles.email,
                });
              }
            });
            
            // Also include the admin themselves if they have a subscription
            // (they might not be in group_members if they're the creator)
            if (user && !uniqueMembers.has(user.id)) {
              const { data: adminProfile } = await supabase
                .from('user_profiles')
                .select('id, full_name, email')
                .eq('id', user.id)
                .maybeSingle();
              
              if (adminProfile) {
                uniqueMembers.set(adminProfile.id, {
                  id: adminProfile.id,
                  user_id: adminProfile.id,
                  full_name: adminProfile.full_name || 'Admin',
                  email: adminProfile.email || '',
                });
              }
            }
            
            fetchedGroupMembers = Array.from(uniqueMembers.values());
            setGroupMembers(fetchedGroupMembers);
            console.log('Group members fetched:', fetchedGroupMembers.length, 'for groups:', Array.from(groupIds));
          } else {
            console.error('Error fetching group members:', membersError);
          }
        } else {
          console.warn('No groups found for group admin');
          setGroupMembers([]);
        }
      }

      // Fetch subscriptions AFTER group members are fetched
      await fetchSubscriptions(fetchedGroupMembers);
    } catch (err: any) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptions = async (membersToUse?: GroupMember[]) => {
    try {
      // Use provided members or state members
      const members = membersToUse || groupMembers;
      let memberIds: string[] = [];
      
      // For regular admins, only show their OWN subscription (not group members)
      if (userProfile?.role !== 'super_admin') {
        if (user) {
          // Group admin should only see their own subscription
          memberIds = [user.id];
          console.log('Group admin: Showing only own subscription for user:', user.id);
        } else {
          // If no user, show empty
          console.warn('No user found, showing empty subscriptions');
          setSubscriptions([]);
          return;
        }
      }

      let query = supabase
        .from('user_subscriptions')
        .select(`
          id,
          user_id,
          plan_id,
          subscription_status,
          access_status,
          start_date,
          end_date,
          payment_due_date,
          access_locked,
          user:user_id (
            id,
            full_name,
            email
          ),
          plan:subscription_plans!plan_id (
            id,
            plan_name,
            plan_type,
            price_vatu
          )
        `)
        .order('created_at', { ascending: false });

      // Filter by group members for regular admins - MUST filter
      if (userProfile?.role !== 'super_admin') {
        if (memberIds.length > 0) {
          query = query.in('user_id', memberIds);
        } else {
          // No members = no subscriptions to show
          setSubscriptions([]);
          return;
        }
      }

      if (filter !== 'all') {
        if (filter === 'expired') {
          query = query.lt('end_date', new Date().toISOString());
        } else {
          query = query.eq('access_status', filter);
        }
      }

      const { data, error } = await query;

      if (error) throw error;

      // Transform data to match interface
      const transformed = (data || []).map((sub: any) => ({
        ...sub,
        plan: {
          id: sub.plan?.id || sub.plan_id,
          name: sub.plan?.plan_name || 'Unknown',
          plan_name: sub.plan?.plan_name || 'Unknown',
          price_vatu: sub.plan?.price_vatu || 0,
        },
      }));

      setSubscriptions(transformed);
    } catch (err: any) {
      console.error('Error fetching subscriptions:', err);
    }
  };

  const handleLockToggle = async (subscriptionId: string, currentLockStatus: boolean) => {
    try {
      const newStatus = currentLockStatus ? 'active' : 'locked';
      await updateSubscriptionAccess(
        subscriptionId,
        newStatus,
        !currentLockStatus ? 'Manually locked by admin' : 'Unlocked by admin'
      );

      // Refresh subscriptions
      await fetchSubscriptions();
    } catch (err: any) {
      console.error('Error toggling lock:', err);
      alert('Failed to toggle subscription lock');
    }
  };

  const handleCreateSubscription = async () => {
    if (!selectedMemberId || !selectedPlanId) {
      alert('Please select a member and plan');
      return;
    }

    try {
      await createSubscription(selectedMemberId, selectedPlanId);
      setShowCreateModal(false);
      setSelectedMemberId('');
      setSelectedPlanId('');
      await fetchSubscriptions();
      alert('Subscription created successfully');
    } catch (err: any) {
      console.error('Error creating subscription:', err);
      alert('Failed to create subscription: ' + err.message);
    }
  };

  const handleExtendSubscription = async () => {
    if (!selectedSubscription || !extendDays) {
      alert('Please select a subscription and enter extension days');
      return;
    }

    try {
      // Use the safe extension function that handles invalid dates
      const { data, error } = await supabase.rpc('extend_subscription_safe', {
        subscription_id_param: selectedSubscription.id,
        days_to_add: extendDays,
      });

      if (error) throw error;

      if (data?.error) {
        throw new Error(data.error);
      }

      setShowExtendModal(false);
      setSelectedSubscription(null);
      setExtendDays(30);
      await fetchSubscriptions();
      alert('Subscription extended successfully');
    } catch (err: any) {
      console.error('Error extending subscription:', err);
      alert('Failed to extend subscription: ' + err.message);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-success bg-success/10';
      case 'expired': return 'text-destructive bg-destructive/10';
      case 'pending': return 'text-warning bg-warning/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-muted rounded"></div>
            <div className="h-20 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-foreground">Subscription Management</h2>
          <div className="flex gap-2">
            {(['all', 'active', 'expired', 'pending'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-3 py-1 rounded-sm text-sm font-medium transition-interactive ${
                  filter === status
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
            {/* Only show "Assign Subscription" button for super admins */}
            {userProfile?.role === 'super_admin' && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-1.5 bg-primary text-primary-foreground rounded-sm text-sm font-medium hover:bg-primary/90 transition-interactive flex items-center gap-2"
              >
                <Icon name="Plus" size={16} />
                Assign Subscription
              </button>
            )}
          </div>
        </div>
        {userProfile?.role === 'super_admin' && (
          <p className="text-sm text-muted-foreground">
            Managing subscriptions for {groupMembers.length} group member{groupMembers.length !== 1 ? 's' : ''}
          </p>
        )}
        {userProfile?.role !== 'super_admin' && (
          <p className="text-sm text-muted-foreground">
            Your subscription
          </p>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Plan
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Dates
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {subscriptions.map((subscription) => (
              <tr key={subscription.id} className="hover:bg-muted/30">
                <td className="px-6 py-4">
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      {subscription.user?.full_name || 'Unknown User'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {subscription.user?.email}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      {subscription.plan?.plan_name || 'Unknown Plan'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {subscription.plan?.price_vatu} VT
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium ${getStatusColor(subscription.subscription_status)}`}>
                    {subscription.subscription_status}
                  </span>
                  {subscription.access_locked && (
                    <span className="ml-2 inline-flex items-center text-destructive">
                      <Icon name="Lock" size={14} />
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-muted-foreground">
                  <div className="space-y-1">
                    <div>Start: {new Date(subscription.start_date).toLocaleDateString()}</div>
                    <div>End: {new Date(subscription.end_date).toLocaleDateString()}</div>
                    {subscription.payment_due_date && (
                      <div className="text-warning">
                        Due: {new Date(subscription.payment_due_date).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleLockToggle(subscription.id, subscription.access_locked)}
                      className={`px-3 py-1 rounded-sm text-xs font-medium transition-interactive ${
                        subscription.access_locked
                          ? 'bg-success/10 text-success hover:bg-success/20' 
                          : 'bg-destructive/10 text-destructive hover:bg-destructive/20'
                      }`}
                    >
                      {subscription.access_locked ? 'Unlock' : 'Lock'}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedSubscription(subscription);
                        setShowExtendModal(true);
                      }}
                      className="px-3 py-1 rounded-sm text-xs font-medium transition-interactive bg-primary/10 text-primary hover:bg-primary/20"
                    >
                      Extend
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {subscriptions.length === 0 && (
          <div className="p-8 text-center text-muted-foreground">
            No subscriptions found
          </div>
        )}
      </div>

      {/* Create Subscription Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">Assign Subscription</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Select Member
                </label>
                <select
                  value={selectedMemberId}
                  onChange={(e) => setSelectedMemberId(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-sm bg-background text-foreground"
                >
                  <option value="">Choose a member...</option>
                  {groupMembers.map((member) => (
                    <option key={member.id} value={member.user_id}>
                      {member.full_name} ({member.email})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Select Plan
                </label>
                <select
                  value={selectedPlanId}
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-sm bg-background text-foreground"
                >
                  <option value="">Choose a plan...</option>
                  {subscriptionPlans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} - {plan.price_vatu} VT ({plan.duration_days} days)
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={handleCreateSubscription}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-sm font-medium hover:bg-primary/90 transition-interactive"
              >
                Create Subscription
              </button>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setSelectedMemberId('');
                  setSelectedPlanId('');
                }}
                className="flex-1 px-4 py-2 border border-border rounded-sm font-medium hover:bg-muted transition-interactive"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extend Subscription Modal */}
      {showExtendModal && selectedSubscription && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">Extend Subscription</h3>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  Member: <span className="text-foreground font-medium">{selectedSubscription.user?.full_name}</span>
                </p>
                <p className="text-sm text-muted-foreground mb-2">
                  Current End Date: <span className="text-foreground font-medium">
                    {new Date(selectedSubscription.end_date).toLocaleDateString()}
                  </span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Extend by (days)
                </label>
                <input
                  type="number"
                  value={extendDays}
                  onChange={(e) => setExtendDays(parseInt(e.target.value) || 30)}
                  min="1"
                  className="w-full px-3 py-2 border border-border rounded-sm bg-background text-foreground"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  New end date: {new Date(new Date(selectedSubscription.end_date).getTime() + extendDays * 24 * 60 * 60 * 1000).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={handleExtendSubscription}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-sm font-medium hover:bg-primary/90 transition-interactive"
              >
                Extend Subscription
              </button>
              <button
                onClick={() => {
                  setShowExtendModal(false);
                  setSelectedSubscription(null);
                  setExtendDays(30);
                }}
                className="flex-1 px-4 py-2 border border-border rounded-sm font-medium hover:bg-muted transition-interactive"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;